import { Box, Flex, Text } from "@chakra-ui/react";
import antaiaLogo from "../../../../../assets/img/antaia-logo.png";

/**
 * A live, client-rendered mirror of `task-reminder.html` (the production email template — see the
 * design handoff) — NOT a reuse of that HTML/Outlook-table markup, which only matters once an actual
 * email is sent server-side. This is plain Chakra/JSX so the compose modal can re-render it on every
 * keystroke with no network round trip.
 *
 * Fixed content (logo, heading, task card, button, footer) is NOT exposed as props to edit — only
 * `message`, `rows`, `ccNames` and the few other values that genuinely change per send are passed in.
 */
export default function ReminderEmailPreview({
  assigneeFirstName,
  message,
  taskTitle,
  rows,
  taskLink,
  senderName,
  senderEmail,
  ccNames = [],
}) {
  // Split on blank lines (paragraphs), keep single line breaks within one — same rule the brief gives
  // the server-side template. No HTML-escaping needed here: JSX text content is escaped by React, not
  // interpreted as markup, so this is already safe by construction.
  const paragraphs = message
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="10px" overflow="hidden">
      <Box px={9} pt="30px" pb={0}>
        <Box as="img" src={antaiaLogo} alt="Antaia" h="22px" w="auto" display="block" />
        <Box mt={7} w="36px" h="3px" bg="lime" borderRadius="2px" />
        <Text mt={3.5} fontSize="22px" lineHeight="1.3" fontWeight="800" color="darkTeal">
          Reminder to complete your Antaia task
        </Text>
      </Box>

      <Box px={9} pt={6} pb={2}>
        <Text fontSize="14.5px" lineHeight="1.65" color="gray.700" mb={3.5}>
          Hi {assigneeFirstName},
        </Text>

        {paragraphs.map((text, i) => (
          <Text key={i} fontSize="14.5px" lineHeight="1.65" color="gray.700" whiteSpace="pre-line" mb={3.5}>
            {text}
          </Text>
        ))}

        <Box mt="22px" border="1px solid" borderColor="gray.200" borderRadius="10px" overflow="hidden">
          <Box px="18px" py={3.5} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
            <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" textTransform="uppercase" color="gray.500">
              Your task
            </Text>
            <Text mt={1} fontSize="16px" fontWeight="700" color="darkTeal">
              {taskTitle}
            </Text>
          </Box>
          <Box px="18px" py={0.5}>
            {rows.map((row, i) => (
              <Flex
                key={row.label}
                justify="space-between"
                gap={4}
                py={2.5}
                borderBottom={i === rows.length - 1 ? "none" : "1px solid"}
                borderColor="gray.100"
                fontSize="13.5px"
                lineHeight="1.4"
              >
                <Text color="gray.500">{row.label}</Text>
                <Text fontWeight="700" color="darkTeal" textAlign="right">
                  {row.value}
                </Text>
              </Flex>
            ))}
          </Box>
        </Box>

        <Box mt={6}>
          {/* Plain Box, not a real Button — this is preview content, never clicked. */}
          <Box
            display="inline-block"
            bg="darkTeal"
            color="white"
            fontSize="14px"
            fontWeight="700"
            px="26px"
            py={3.5}
            borderRadius="8px"
          >
            Open task in Antaia
          </Box>
          <Text mt={2.5} fontSize="12px" color="gray.400">
            Or copy this link: {taskLink}
          </Text>
        </Box>

        <Box mt="26px" pt={4} pb="18px" borderTop="1px solid" borderColor="gray.100" fontSize="13px" lineHeight="1.6" color="gray.600">
          <Text as="span">
            Sent by <Text as="span" fontWeight="700" color="darkTeal">{senderName}</Text> ({senderEmail}) via
            Antaia.
          </Text>
          {ccNames.length > 0 && (
            <Text mt={1}>In copy: {ccNames.join(", ")}</Text>
          )}
        </Box>
      </Box>

      <Box px={9} py="18px" bg="gray.50" borderTop="1px solid" borderColor="gray.200" fontSize="11.5px" lineHeight="1.6" color="gray.400">
        <Text>
          You&apos;re receiving this because you&apos;re assigned to this data task in Antaia. Reply to this
          email to reach {senderName.split(" ")[0]}.
        </Text>
        <Text mt={2} fontWeight="700" color="gray.500">
          © {new Date().getFullYear()} Antaia · antaia.io
        </Text>
      </Box>
    </Box>
  );
}
